import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { DEV_SESSION_STORAGE_KEY } from '@constants/index';

/**
 * Storybook-only Cognito sign-in, mirroring palmetto-typescript-portal's
 * cognitoThunk: USER_PASSWORD_AUTH against the palmetto user pool, returning
 * the JWT id_token the app (and its API) authenticate with.
 */
const USER_POOL_ID: string = import.meta.env.VITE_APP_COGNITO_USER_POOL_ID || 'us-east-1_3f1efBpoo';
const CLIENT_ID: string =
  import.meta.env.VITE_APP_COGNITO_CLIENT_ID || '4i0bpro8bq9cusqkigt1j4gb9f';

const STORAGE_KEY = DEV_SESSION_STORAGE_KEY;

export const loginWithCognito = (username: string, password: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const pool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
    const user = new CognitoUser({ Username: username.trim(), Pool: pool });
    // Same flow the portal uses - required to trigger user migration.
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');
    user.authenticateUser(
      new AuthenticationDetails({ Username: username.trim(), Password: password }),
      {
        onSuccess: (session) => {
          const jwt = session.getIdToken().getJwtToken();
          sessionStorage.setItem(STORAGE_KEY, jwt);
          resolve(jwt);
        },
        onFailure: (error: Error) => {
          reject(new Error(error?.message || 'Incorrect username or password.'));
        },
        newPasswordRequired: () => {
          reject(
            new Error('This account requires a password change. Sign in through the portal first.'),
          );
        },
      },
    );
  });

/** Returns the cached id_token if it has more than a minute of life left. */
export const getCachedJwt = (): string | null => {
  const jwt = sessionStorage.getItem(STORAGE_KEY);
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]));
    if (typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() + 60_000) return jwt;
  } catch {
    // corrupt cache - fall through and clear
  }
  sessionStorage.removeItem(STORAGE_KEY);
  return null;
};

export const clearCachedJwt = () => sessionStorage.removeItem(STORAGE_KEY);
